/* Shared luxury portal UI (Phase B) — reused by Nanak Operations, Corporate Client and
   the Super Admin shell. Presentational only; data + handlers are passed in by the page. */
import { useEffect, useRef, useState } from "react";
import {
  CalendarDays, Car, Search, ChevronDown, ChevronRight, MapPin, Plane, AlertCircle,
  Navigation, Activity, Users,
  Flag, Dot, Armchair, Briefcase, Clock,
} from "lucide-react";
import { hasGoogleMaps, loadGoogleMaps, DARK_MAP_STYLE, goldPin } from "@/lib/googleMaps";

export const ESCALADE = "/brand/escalade-hero.jpg";

export const Eyebrow = ({ children }) => (
  <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold-dark">{children}</span>
);
export const CardLabel = ({ icon: Icon, children, right }) => (
  <div className="mb-4 flex items-center justify-between">
    <div className="flex items-center gap-2">{Icon && <Icon className="h-3.5 w-3.5 text-gold-light" />}<span className="text-sm font-semibold uppercase tracking-widest text-gold-light">{children}</span></div>
    {right}
  </div>
);
export const Panel = ({ className = "", children }) => (
  <div className={`rounded-2xl border border-gold/15 bg-obsidian-800 p-6 ${className}`}>{children}</div>
);
export const Missing = ({ field }) => (
  <span data-testid={`missing-data-badge-${field}`} className="inline-flex items-center gap-1.5 rounded-md border border-amber-800/50 bg-amber-950/30 px-2.5 py-1 text-xs text-amber-300"><AlertCircle className="h-3 w-3" /> Not provided</span>
);
const TONES = {
  offers: "text-[#F59E0B] bg-[#F59E0B]/12 border-[#F59E0B]/35", upcoming: "text-[#60A5FA] bg-[#60A5FA]/12 border-[#60A5FA]/35",
  active: "text-gold-light bg-gold/16 border-gold/50", completed: "text-[#E2E8F0] bg-[#E2E8F0]/10 border-[#E2E8F0]/25",
  canceled: "text-[#F87171] bg-[#F87171]/12 border-[#F87171]/35", review: "text-[#E2B340] bg-[#E2B340]/12 border-[#E2B340]/35",
};
export const StatusChip = ({ tone = "upcoming", label }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${TONES[tone] || TONES.upcoming}`}><Dot className="h-3 w-3" strokeWidth={6} />{label}</span>
);
export const Row = ({ k, children }) => (
  <div className="flex items-center justify-between border-b border-line/60 py-3 last:border-0"><span className="text-sm text-muted-steel">{k}</span><span className="text-right text-sm font-medium text-ivory">{children}</span></div>
);

/* map a reservation record to a display status chip (honest — never implies unknown detail) */
const ACTIVE = { driver_en_route: "Chauffeur en route", driver_arrived: "Chauffeur arrived", passenger_on_board: "Passenger on board", in_progress: "Trip in progress" };
export function statusMeta(r) {
  if (r.acceptance_state === "offer" || r.acceptance_state === "unknown") return { tone: "offers", label: "Offer pending" };
  if (r.review_status === "unmatched") return { tone: "review", label: "Unmatched" };
  const s = r.status;
  if (s === "canceled") return { tone: "canceled", label: "Canceled" };
  if (s === "completed") return { tone: "completed", label: "Completed" };
  if (ACTIVE[s]) return { tone: "active", label: ACTIVE[s] };
  if (s === "driver_assigned") return { tone: "upcoming", label: "Chauffeur assigned" };
  if (s === "confirmed") return { tone: "upcoming", label: "Confirmed" };
  return { tone: "upcoming", label: s || "Pending" };
}
export const fmtDate = (v) => {
  if (!v) return null;
  try { return new Date(v).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return String(v); }
};

/* Styled dark visual panel — shown when no Google Maps key is configured. */
const MapPlaceholder = ({ className = "h-64", network = true, mobile = false }) => (
  <div data-testid="route-map-panel" className={`relative w-full overflow-hidden rounded-xl border border-gold/20 bg-[#0A0B0E] ${className}`}>
    <div className="absolute inset-0 opacity-[0.16]" style={{ backgroundImage: "linear-gradient(rgba(120,125,135,.5) 1px,transparent 1px),linear-gradient(90deg,rgba(120,125,135,.5) 1px,transparent 1px)", backgroundSize: "30px 30px" }} />
    <div className="absolute left-0 top-1/3 h-[3px] w-full -rotate-6 bg-[#171920]" />
    <div className="absolute left-[28%] top-0 h-full w-[3px] rotate-3 bg-[#171920]" />
    {network && (
      <svg className="absolute inset-0 h-full w-full opacity-70" preserveAspectRatio="none" viewBox="0 0 480 260">
        <path d="M30 220 Q 180 60 360 120" stroke="#D4AF37" strokeWidth="0.8" fill="none" opacity="0.35" />
        <path d="M60 240 Q 260 120 450 70" stroke="#E5C158" strokeWidth="0.8" fill="none" opacity="0.3" />
        {[[360, 120], [450, 70], [200, 40], [120, 200]].map(([x, y], i) => (<circle key={i} cx={x} cy={y} r="2" fill="#E5C158" opacity="0.7" />))}
      </svg>
    )}
    <svg className="absolute inset-0 h-full w-full" preserveAspectRatio="none" viewBox="0 0 480 260">
      <path d="M62 206 C 170 160 220 120 292 96 S 404 62 446 50" stroke="#E5C158" strokeWidth="3" fill="none" strokeLinecap="round" strokeDasharray="1 9" />
    </svg>
    <div className="absolute left-[9%] bottom-[16%]"><span className="flex h-6 w-6 items-center justify-center rounded-full border border-gold bg-obsidian-900 shadow-[0_0_12px_2px_rgba(212,175,55,.5)]"><Plane className="h-3 w-3 text-gold-light" /></span></div>
    <div className="absolute right-[6%] top-[14%]"><Flag className="h-5 w-5 text-gold-light" /></div>
    <span className="absolute bottom-2 right-3 text-[10px] text-dim-charcoal">Map preview · add a Google Maps key to enable live routing</span>
  </div>
);

/* Live dark-styled Google Map. Plots pickup/drop-off markers and a gold driving
   route when addresses are available; otherwise shows a dark service-area view.
   Read-only — never writes to Limo Anywhere. */
const GOOGLE_DEFAULT_CENTER = { lat: 42.3601, lng: -71.0589 }; // service-area default view

const GoogleRouteMap = ({ className = "h-64", mobile = false, pickup = "", dropoff = "" }) => {
  const containerRef = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps()
      .then((maps) => {
        if (cancelled || !containerRef.current) return;
        const map = new maps.Map(containerRef.current, {
          center: GOOGLE_DEFAULT_CENTER,
          zoom: 10,
          styles: DARK_MAP_STYLE,
          disableDefaultUI: true,
          zoomControl: !mobile,
          gestureHandling: "cooperative",
          backgroundColor: "#0a0b0e",
        });

        const geocoder = new maps.Geocoder();
        const geocode = (address) =>
          new Promise((resolve) =>
            geocoder.geocode({ address }, (res, status) =>
              resolve(status === "OK" && res && res[0] ? res[0].geometry.location : null)
            )
          );

        (async () => {
          const pLoc = pickup ? await geocode(pickup) : null;
          const dLoc = dropoff ? await geocode(dropoff) : null;
          if (cancelled) return;

          if (pLoc) new maps.Marker({ position: pLoc, map, icon: goldPin(maps, "#E5C158"), title: "Pickup" });
          if (dLoc) new maps.Marker({ position: dLoc, map, icon: goldPin(maps, "#C7CCD6"), title: "Drop-off" });

          if (pLoc && dLoc) {
            const svc = new maps.DirectionsService();
            const renderer = new maps.DirectionsRenderer({
              map,
              suppressMarkers: true,
              preserveViewport: false,
              polylineOptions: { strokeColor: "#E5C158", strokeWeight: 4, strokeOpacity: 0.9 },
            });
            svc.route(
              { origin: pLoc, destination: dLoc, travelMode: maps.TravelMode.DRIVING },
              (result, status) => {
                if (cancelled) return;
                if (status === "OK") {
                  renderer.setDirections(result);
                } else {
                  const b = new maps.LatLngBounds();
                  b.extend(pLoc); b.extend(dLoc);
                  map.fitBounds(b, 48);
                }
              }
            );
          } else if (pLoc || dLoc) {
            map.setCenter(pLoc || dLoc);
            map.setZoom(13);
          }
        })();
      })
      .catch(() => { if (!cancelled) setFailed(true); });

    return () => { cancelled = true; };
  }, [pickup, dropoff, mobile]);

  if (failed) return <MapPlaceholder className={className} mobile={mobile} />;
  return (
    <div className={`relative w-full overflow-hidden rounded-xl border border-gold/20 bg-[#0A0B0E] ${className}`}>
      <div ref={containerRef} data-testid="route-map-panel" className="h-full w-full" />
    </div>
  );
};

export const RouteMap = ({ className = "h-64", network = true, mobile = false, pickup = "", dropoff = "" }) =>
  hasGoogleMaps()
    ? <GoogleRouteMap className={className} mobile={mobile} pickup={pickup} dropoff={dropoff} />
    : <MapPlaceholder className={className} network={network} mobile={mobile} />;

export const VehicleCard = ({ name }) => (
  <Panel>
    <CardLabel icon={Car}>Vehicle</CardLabel>
    {name ? (
      <div className="space-y-4">
        <div className="relative overflow-hidden rounded-xl border border-gold/15">
          <img src={ESCALADE} alt="assigned vehicle" className="h-32 w-full object-cover opacity-80" />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian-800 via-transparent to-transparent" />
          <div className="absolute bottom-2 left-3"><p className="text-sm font-semibold text-ivory">{name}</p></div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {[[Armchair, "—", "Seats"], [Briefcase, "—", "Luggage"], [Clock, "On duty", "Status"]].map(([Ic, v, l]) => (
            <div key={l} className="rounded-lg border border-line bg-obsidian-900 py-2.5 text-center"><Ic className="mx-auto h-4 w-4 text-gold-light" /><p className="mt-1 text-sm text-ivory">{v}</p><p className="text-[10px] text-muted-steel">{l}</p></div>
          ))}
        </div>
      </div>
    ) : (
      <div className="flex h-40 flex-col items-center justify-center text-center"><Car className="h-7 w-7 text-muted-steel" /><p className="mt-2 text-sm text-ivory">No vehicle assigned</p><div className="mt-2"><Missing field="vehicle" /></div></div>
    )}
  </Panel>
);

export const Shell = ({ portal, accentLabel, nav, active, onNav, onSearch, children, rightSlot }) => (
  <div className="flex h-[calc(100vh-0px)] overflow-hidden bg-obsidian-900">
    <aside className="group flex w-16 flex-shrink-0 flex-col border-r border-line bg-obsidian-900 py-4 transition-[width] duration-300 hover:w-60">
      <div className="mb-6 flex items-center gap-3 px-4"><div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-gold/40 font-display text-lg font-bold text-gold-light">N</div><span className="whitespace-nowrap text-sm font-semibold text-ivory opacity-0 transition-opacity group-hover:opacity-100">Nanak</span></div>
      {nav.map(([key, label, Icon]) => (<button key={key} data-testid={`sidebar-${key}`} onClick={() => onNav && onNav(key)} className={`mx-2 mb-1 flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${active === key ? "bg-gold/10 text-gold-light" : "text-muted-steel hover:bg-obsidian-700 hover:text-ivory"}`}><Icon className="h-4.5 w-4.5 flex-shrink-0" /><span className="whitespace-nowrap text-sm opacity-0 transition-opacity group-hover:opacity-100">{label}</span></button>))}
    </aside>
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      <header className="flex h-14 flex-shrink-0 items-center gap-4 border-b border-gold/15 bg-obsidian-800/90 px-5 backdrop-blur-md">
        <div className="flex items-center gap-2 rounded-lg border border-gold/20 px-3 py-1.5"><span className="text-xs font-semibold text-ivory">{portal}</span><ChevronDown className="h-3.5 w-3.5 text-gold-light" /></div>
        <div className="hidden flex-1 lg:block" aria-hidden="true" />
        <div className="flex items-center gap-3">
          {accentLabel && <span className="hidden items-center gap-1.5 rounded-full border border-gold/25 bg-gold/5 px-3 py-1 text-[11px] text-gold-light sm:inline-flex"><Activity className="h-3 w-3" /> LA Sync · {accentLabel}</span>}
          {rightSlot}
        </div>
      </header>
      <main className="min-h-0 flex-1 overflow-y-auto">{children}</main>
    </div>
  </div>
);

export { Search, ChevronRight, Navigation, CalendarDays };
